<script setup lang="ts">
import { ref, watch, onUnmounted } from "vue";

// shared
import usePagination from "@/shared/composables/usePagination";
import Badge from "@/shared/components/atomic/badge/Badge.vue";
import Spinner from "@/shared/components/atomic/svgs/spinner/Spinner.vue";

// features
import type { PendingRegistrationUserListProps } from "@/features/pending-registration/models/interface";
import PendingRegistrationUser from "@/features/pending-registration/ui/PendingRegistrationUser.vue";

const props = defineProps<PendingRegistrationUserListProps>();

const rootId = ref(`pending-registration-user-list-${Math.random().toString(36).substring(2, 9)}`);
const intersectionRef = ref<HTMLDivElement | null>(null);

const { reinitialize, destroy } = usePagination({
    targetRef: intersectionRef,
    callback: props.nextPageRequest || (() => Promise.resolve()),
    options: {
        get root() {
            return document.getElementById(rootId.value);
        },
        threshold: 0.1,
    },
});

watch(intersectionRef, () => {
    if (intersectionRef.value) {
        reinitialize();
    }
});

onUnmounted(() => {
    destroy();
});
</script>

<template>
    <div
        class="flex justify-center items-center p-medium rounded-large2 bg-surface-white shadow-[0px_16px_24px_0px_rgba(0,0,0,0.12),0px_0px_2px_0px_rgba(0,0,0,0.08)]"
    >
        <div class="flex flex-col w-[30rem] gap-medium">
            <div class="flex justify-between items-center w-full">
                <p class="font-bold font-pretendard text-heading-medium text-t-basic">승인대기</p>
                <Badge type="solid" color="primary" size="large">{{ users.length }}명</Badge>
            </div>
            <div :id="rootId" class="w-full flex flex-col gap-[1.2rem] h-[22.4rem] overflow-y-auto">
                <PendingRegistrationUser
                    v-for="user in users"
                    :key="user.userId"
                    :registration-id="user.registrationId"
                    :user-id="user.userId"
                    :name="user.name"
                    :type="user.type"
                    :un-converted-type="user.unConvertedType"
                    :registration-date="user.registrationDate"
                />
                <div
                    ref="intersectionRef"
                    class="w-full min-h-[1.2rem] flex justify-center items-center"
                >
                    <Spinner v-if="isNextPageLoading" />
                </div>
            </div>
        </div>
    </div>
</template>
